import React, { useEffect, useMemo, useState } from 'react'
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { ExternalLink, PanelRightClose, PanelRightOpen, Plus, Save, Search, StickyNote, Trash2, X } from 'lucide-react'
import { ArchiveRestore, Pencil, Pin, RotateCcw } from 'lucide-react'
import { getApiErrorMessage } from '../lib/api'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { api } from '../lib/api'
import InsurerSystemsDirectory from '../components/InsurerSystemsDirectory'
import CustomerMemoRichEditor, {
  CustomerMemoAttachmentList,
  CustomerMemoRichContent,
  memoContentToHtml,
} from '../components/CustomerMemoRichEditor'
import { PageFrame, PageHeader } from '../ui/PageLayout'

const LEGACY_TASK_STORAGE_KEY_PREFIX = 'sgez.personalTaskBoard.v1'
const LEGACY_NOTE_STORAGE_KEY_PREFIX = 'sgez.personalTaskNotes.v1'
const LEGACY_IMPORT_MARK_PREFIX = 'sgez.personalWorkDbImported.v1'

const COLUMNS = [
  { key: 'TODO', label: '할 일' },
  { key: 'IN_PROGRESS', label: '진행 중' },
  { key: 'WAITING', label: '대기' },
  { key: 'DONE', label: '완료' },
]

const STATUS_FILTERS = [
  { key: 'ALL', label: '전체' },
  ...COLUMNS,
]

const PRIORITIES = [
  { value: 'NORMAL', label: '보통' },
  { value: 'HIGH', label: '높음' },
  { value: 'LOW', label: '낮음' },
]

const LINK_TYPES = [
  { value: 'NONE', label: '연결 안 함' },
  { value: 'CUSTOMER', label: '고객' },
  { value: 'CLAIM', label: '청구 신청' },
  { value: 'REQUEST', label: '설계 의뢰' },
  { value: 'CALENDAR', label: '일정관리' },
  { value: 'EXTERNAL', label: '외부 링크' },
  { value: 'MANUAL', label: '직접 입력' },
]

const EMPTY_FORM = {
  title: '',
  memo: '',
  priority: 'NORMAL',
  dueDate: '',
  linkType: 'NONE',
  relatedId: '',
  linkLabel: '',
  linkUrl: '',
}

const EMPTY_NOTE_FORM = {
  title: '',
  body: '',
  content: null,
  attachments: [],
  tagsText: '',
  pinned: false,
}

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function pageItems(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.content)) return data.content
  return []
}

function normalizeUrl(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  if (text.startsWith('/')) return text
  if (/^https?:\/\//i.test(text)) return text
  return `https://${text}`
}

function normalizeTaskLinkUrl(linkType, value) {
  const text = String(value || '').trim()
  if (!text || linkType === 'NONE') return ''
  if (text.startsWith('/') || /^https?:\/\//i.test(text)) return text
  if (linkType === 'MANUAL') return `/${text}`
  return normalizeUrl(text)
}

function todayText() {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function dueTone(dueDate, status) {
  if (!dueDate || status === 'DONE') return 'text-slate-500'
  const today = todayText()
  if (dueDate < today) return 'text-red-600'
  if (dueDate === today) return 'text-amber-700'
  return 'text-slate-500'
}

function priorityClass(priority) {
  if (priority === 'HIGH') return 'border-red-200 bg-red-50 text-red-700'
  if (priority === 'LOW') return 'border-slate-200 bg-slate-50 text-slate-600'
  return 'border-sky-200 bg-sky-50 text-sky-700'
}

function columnLabel(status) {
  return COLUMNS.find((column) => column.key === status)?.label || status || ''
}

function linkTypeLabel(linkType) {
  return LINK_TYPES.find((item) => item.value === linkType)?.label || ''
}

function formatNoteTime(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`
}

function normalizeTaskFromApi(row) {
  return {
    id: row?.id,
    title: row?.title || '',
    memo: row?.memo || '',
    status: row?.status || 'TODO',
    priority: row?.priority || 'NORMAL',
    dueDate: row?.dueDate || '',
    linkType: row?.linkType || 'NONE',
    relatedId: row?.relatedId || '',
    linkLabel: row?.linkLabel || '',
    linkUrl: row?.linkUrl || '',
    sortOrder: row?.sortOrder || 0,
    createdAt: row?.createdAt || '',
    updatedAt: row?.updatedAt || '',
    completedAt: row?.completedAt || null,
    deletedAt: row?.deletedAt || null,
    expiresAt: row?.expiresAt || null,
  }
}

function normalizeNoteFromApi(row) {
  const plainText = row?.plainText ?? row?.body ?? ''
  return {
    id: row?.id,
    title: row?.title || '',
    body: plainText,
    content: row?.content && typeof row.content === 'object' ? row.content : null,
    schemaVersion: Number(row?.schemaVersion) || 1,
    pinned: row?.pinned === true,
    tags: Array.isArray(row?.tags) ? row.tags.filter(Boolean) : [],
    attachments: Array.isArray(row?.attachments) ? row.attachments.filter((item) => item?.fileId) : [],
    attachmentCount: Number(row?.attachmentCount) || 0,
    createdAt: row?.createdAt || '',
    updatedAt: row?.updatedAt || '',
  }
}

function sortNotes(rows) {
  return [...rows].sort((a, b) => {
    const pinned = Number(b?.pinned === true) - Number(a?.pinned === true)
    if (pinned !== 0) return pinned
    return new Date(b?.updatedAt || b?.createdAt || 0).getTime()
      - new Date(a?.updatedAt || a?.createdAt || 0).getTime()
  })
}

function parseNoteTags(value) {
  const seen = new Set()
  return String(value || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => {
      const key = tag.toLowerCase()
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, 12)
}

function noteAttachmentPayload(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((item) => item?.fileId)
    .map((item, index) => ({
      fileId: item.fileId,
      caption: item.caption || '',
      sortOrder: index,
    }))
}

function readLegacyPersonalItems(prefix, userKey) {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(`${prefix}.${String(userKey || 'anonymous')}`)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function legacyImportMarked(userKey) {
  if (typeof window === 'undefined') return true
  try {
    return window.localStorage.getItem(`${LEGACY_IMPORT_MARK_PREFIX}.${String(userKey || 'anonymous')}`) === '1'
  } catch {
    return true
  }
}

function markLegacyImported(userKey) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(`${LEGACY_IMPORT_MARK_PREFIX}.${String(userKey || 'anonymous')}`, '1')
  } catch {
    // ignore
  }
}

async function importLegacyPersonalWorkIfNeeded(userKey, currentTasks, currentNotes) {
  if (legacyImportMarked(userKey)) {
    return { tasks: currentTasks, notes: currentNotes }
  }

  const legacyTasks = currentTasks.length > 0
    ? []
    : readLegacyPersonalItems(LEGACY_TASK_STORAGE_KEY_PREFIX, userKey).filter((task) => task?.title)
  const legacyNotes = currentNotes.length > 0
    ? []
    : readLegacyPersonalItems(LEGACY_NOTE_STORAGE_KEY_PREFIX, userKey).filter((note) => note?.body)

  if (legacyTasks.length === 0 && legacyNotes.length === 0) {
    markLegacyImported(userKey)
    return { tasks: currentTasks, notes: currentNotes }
  }

  const importedTasks = []
  for (const task of legacyTasks) {
    try {
      const linkType = task.linkType || (task.linkUrl ? 'MANUAL' : 'NONE')
      const res = await api.post('/work/personal-tasks', {
        title: String(task.title || '').trim(),
        memo: String(task.memo || '').trim(),
        status: task.status || 'TODO',
        priority: task.priority || 'NORMAL',
        dueDate: task.dueDate || null,
        linkType,
        relatedId: task.relatedId || '',
        linkLabel: task.linkLabel || '',
        linkUrl: normalizeTaskLinkUrl(linkType, task.linkUrl),
      })
      importedTasks.push(normalizeTaskFromApi(unwrap(res)))
    } catch {
      // ignore single-item import failures
    }
  }

  const importedNotes = []
  for (const note of legacyNotes) {
    try {
      const body = String(note.body || '').trim()
      if (!body) continue
      const res = await api.post('/work/personal-notes', { body })
      importedNotes.push(normalizeNoteFromApi(unwrap(res)))
    } catch {
      // ignore single-item import failures
    }
  }

  markLegacyImported(userKey)
  return {
    tasks: importedTasks.length > 0 ? importedTasks : currentTasks,
    notes: importedNotes.length > 0 ? importedNotes : currentNotes,
  }
}

function customerOptionLabel(row) {
  const name = row?.customerName || '고객'
  return row?.customerPhone ? `${name} · ${row.customerPhone}` : name
}

function claimOptionLabel(row) {
  return [row?.customerName, row?.insurerOrgName].filter(Boolean).join(' / ') || `청구 신청 ${row?.id || ''}`.trim()
}

function requestOptionLabel(row) {
  return row?.title || `설계 의뢰 ${row?.id || ''}`.trim()
}

function RelatedTargetFields({ value, onChange }) {
  const linkType = value.linkType || (value.linkUrl ? 'MANUAL' : 'NONE')
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const debouncedQuery = useDebouncedValue(query, 250)
  const needsSearch = linkType === 'CUSTOMER' || linkType === 'CLAIM' || linkType === 'REQUEST'

  useEffect(() => {
    setQuery('')
    setOptions([])
  }, [linkType])

  useEffect(() => {
    if (!needsSearch) return undefined
    let alive = true
    async function loadOptions() {
      setLoading(true)
      try {
        let res
        if (linkType === 'CUSTOMER') {
          res = await api.get('/customer-management/customers', {
            params: { q: debouncedQuery || undefined, page: 0, size: 8 },
            __silent: true,
          })
        } else if (linkType === 'CLAIM') {
          res = await api.get('/claims/applications/list', {
            params: { customerName: debouncedQuery || undefined, page: 0, size: 8 },
            __silent: true,
          })
        } else {
          res = await api.get('/requests', {
            params: { q: debouncedQuery || undefined, page: 0, size: 8, sort: '-createdAt' },
            __silent: true,
          })
        }
        if (alive) setOptions(pageItems(unwrap(res)).slice(0, 8))
      } catch {
        if (alive) setOptions([])
      } finally {
        if (alive) setLoading(false)
      }
    }
    loadOptions()
    return () => { alive = false }
  }, [debouncedQuery, linkType, needsSearch])

  const setLinkType = (nextType) => {
    if (nextType === 'NONE') {
      onChange({ ...value, linkType: 'NONE', relatedId: '', linkLabel: '', linkUrl: '' })
      return
    }
    if (nextType === 'CALENDAR') {
      onChange({ ...value, linkType: 'CALENDAR', relatedId: '', linkLabel: '일정관리', linkUrl: '/work/calendar' })
      return
    }
    onChange({ ...value, linkType: nextType, relatedId: '', linkLabel: '', linkUrl: '' })
  }

  const selectRelated = (row) => {
    if (!row?.id) return
    if (linkType === 'CUSTOMER') {
      onChange({
        ...value,
        linkType,
        relatedId: String(row.id),
        linkLabel: `${row.customerName || '고객'} 고객`,
        linkUrl: `/support/customers/${row.id}`,
      })
      return
    }
    if (linkType === 'CLAIM') {
      const label = claimOptionLabel(row)
      onChange({
        ...value,
        linkType,
        relatedId: String(row.id),
        linkLabel: `${label} 청구`,
        linkUrl: `/support/claims/${row.id}`,
      })
      return
    }
    const label = requestOptionLabel(row)
    onChange({
      ...value,
      linkType,
      relatedId: String(row.id),
      linkLabel: `${label} 설계 의뢰`,
      linkUrl: `/requests/${row.id}`,
    })
  }

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
      <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">관련 화면</span>
          <select
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={linkType}
            onChange={(e) => setLinkType(e.target.value)}
          >
            {LINK_TYPES.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>

        {needsSearch ? (
          <div className="min-w-0">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">검색</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`${linkTypeLabel(linkType)} 검색`}
              />
            </label>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
              {loading ? (
                <div className="px-3 py-2 text-xs text-slate-500">검색 중...</div>
              ) : options.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">선택할 항목이 없습니다.</div>
              ) : (
                options.map((row) => {
                  const label = linkType === 'CUSTOMER'
                    ? customerOptionLabel(row)
                    : linkType === 'CLAIM'
                      ? claimOptionLabel(row)
                      : requestOptionLabel(row)
                  const selected = String(value.relatedId || '') === String(row.id || '')
                  return (
                    <button
                      key={`${linkType}-${row.id}`}
                      type="button"
                      className={`block w-full px-3 py-2 text-left text-xs hover:bg-slate-50 ${selected ? 'bg-sky-50 font-bold text-sgez-primary' : 'text-slate-700'}`}
                      onClick={() => selectRelated(row)}
                    >
                      <span className="line-clamp-2">{label}</span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        ) : null}

        {linkType === 'EXTERNAL' || linkType === 'MANUAL' ? (
          <div className="grid min-w-0 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">표시 이름</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
                value={value.linkLabel}
                onChange={(e) => onChange({ ...value, linkLabel: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">{linkType === 'EXTERNAL' ? 'URL' : '경로'}</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
                value={value.linkUrl}
                onChange={(e) => onChange({ ...value, linkUrl: e.target.value })}
              />
            </label>
          </div>
        ) : null}

        {linkType === 'CALENDAR' ? (
          <div className="flex items-end">
            <div className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
              기한 날짜에 일정 등록
            </div>
          </div>
        ) : null}
      </div>
      {value.linkUrl ? (
        <div className="mt-2 truncate text-xs font-semibold text-slate-500">
          {value.linkLabel || value.linkUrl}
        </div>
      ) : null}
    </div>
  )
}

function TaskForm({ value, onChange, onSubmit, submitLabel, onCancel }) {
  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">제목</span>
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={value.title}
            onChange={(e) => onChange({ ...value, title: e.target.value })}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">우선순위</span>
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
              value={value.priority}
              onChange={(e) => onChange({ ...value, priority: e.target.value })}
            >
              {PRIORITIES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">기한</span>
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
              value={value.dueDate}
              onChange={(e) => onChange({ ...value, dueDate: e.target.value })}
            />
          </label>
        </div>
      </div>
      <label className="mt-3 block">
        <span className="text-xs font-semibold text-slate-600">메모</span>
        <textarea
          rows={3}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
          value={value.memo}
          onChange={(e) => onChange({ ...value, memo: e.target.value })}
        />
      </label>
      <RelatedTargetFields value={value} onChange={onChange} />
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          onClick={onCancel}
        >
          취소
        </button>
        <button
          type="submit"
          className="inline-flex items-center gap-1 rounded-lg bg-sgez-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sgez-primary/90"
        >
          <Plus className="h-4 w-4" />
          {submitLabel}
        </button>
      </div>
    </form>
  )
}

function TaskCreateModal({ open, value, onChange, onSubmit, onClose }) {
  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/40 px-4 py-6">
      <div className="absolute inset-0" onClick={onClose} />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="personal-task-create-title"
        className="relative max-h-[calc(100vh-3rem)] w-full max-w-3xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id="personal-task-create-title" className="text-lg font-bold text-slate-900">업무 추가</h2>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            title="닫기"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <TaskForm value={value} onChange={onChange} onSubmit={onSubmit} submitLabel="추가" onCancel={onClose} />
      </section>
    </div>
  )
}

export default function PersonalTaskBoardPage() {
  const { user } = useAuth()
  const userKey = user?.id || user?.userId || user?.email || user?.loginId || 'anonymous'
  const hasUser = Boolean(user)
  const [tasks, setTasks] = useState([])
  const [notes, setNotes] = useState([])
  const [form, setForm] = useState(EMPTY_FORM)
  const [noteDraft, setNoteDraft] = useState(EMPTY_NOTE_FORM)
  const [editingNoteId, setEditingNoteId] = useState(null)
  const [noteSearch, setNoteSearch] = useState('')
  const [notesPanelOpen, setNotesPanelOpen] = useState(false)
  const [noteComposerOpen, setNoteComposerOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [trashOpen, setTrashOpen] = useState(false)
  const [trashTasks, setTrashTasks] = useState([])
  const [trashLoading, setTrashLoading] = useState(false)
  const [trashRetentionDays, setTrashRetentionDays] = useState(30)
  const [credentialsOpen, setCredentialsOpen] = useState(false)
  const [activeDragTask, setActiveDragTask] = useState(null)
  const [selectedMobileStatus, setSelectedMobileStatus] = useState('TODO')
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
  )

  useEffect(() => {
    let alive = true
    async function loadPersonalWork() {
      setLoading(true)
      setError('')
      try {
        const [taskRes, noteRes] = await Promise.all([
          api.get('/work/personal-tasks'),
          api.get('/work/personal-notes'),
        ])
        if (!alive) return
        const loadedTasks = pageItems(unwrap(taskRes)).map(normalizeTaskFromApi)
        const loadedNotes = pageItems(unwrap(noteRes)).map(normalizeNoteFromApi)
        const migrated = await importLegacyPersonalWorkIfNeeded(userKey, loadedTasks, loadedNotes)
        if (!alive) return
        setTasks(migrated.tasks)
        setNotes(sortNotes(migrated.notes))
      } catch {
        if (alive) setError('내 업무보드를 불러오지 못했습니다.')
      } finally {
        if (alive) setLoading(false)
      }
    }
    if (hasUser) {
      loadPersonalWork()
    } else {
      setTasks([])
      setNotes([])
      setLoading(false)
    }
    return () => { alive = false }
  }, [hasUser, userKey])

  const counts = useMemo(() => {
    return Object.fromEntries(COLUMNS.map((column) => [
      column.key,
      tasks.filter((task) => task.status === column.key).length,
    ]))
  }, [tasks])

  const filteredNotes = useMemo(() => {
    const query = noteSearch.trim().toLowerCase()
    if (!query) return notes
    return notes.filter((note) => [
      note.title,
      note.body,
      ...(Array.isArray(note.tags) ? note.tags : []),
    ].some((value) => String(value || '').toLowerCase().includes(query)))
  }, [noteSearch, notes])

  const mobileTasks = useMemo(() => {
    if (selectedMobileStatus === 'ALL') return tasks
    return tasks.filter((task) => task.status === selectedMobileStatus)
  }, [selectedMobileStatus, tasks])

  const addTask = async (e) => {
    e.preventDefault()
    const title = form.title.trim()
    if (!title) return
    if ((form.linkType || 'NONE') === 'CALENDAR' && !form.dueDate) {
      setError('일정 등록은 기한을 먼저 선택해야 합니다.')
      return
    }
    setError('')
    try {
      const payload = {
        title,
        memo: form.memo.trim(),
        priority: form.priority,
        dueDate: form.dueDate || null,
        linkType: form.linkType || 'NONE',
        relatedId: form.relatedId || '',
        linkLabel: form.linkLabel.trim(),
        linkUrl: normalizeTaskLinkUrl(form.linkType || 'NONE', form.linkUrl),
        status: 'TODO',
      }
      const res = await api.post('/work/personal-tasks', payload)
      const saved = normalizeTaskFromApi(unwrap(res))
      setTasks((prev) => [saved, ...prev.filter((task) => String(task.id) !== String(saved.id))])
      setForm(EMPTY_FORM)
      setCreateOpen(false)
    } catch {
      setError('업무를 저장하지 못했습니다.')
    }
  }

  const addNote = async (e) => {
    e.preventDefault()
    const body = noteDraft.body.trim()
    const attachments = noteAttachmentPayload(noteDraft.attachments)
    if (!body && attachments.length === 0) return
    setError('')
    try {
      const payload = {
        title: noteDraft.title.trim(),
        body,
        plainText: body,
        content: noteDraft.content,
        pinned: noteDraft.pinned,
        tags: parseNoteTags(noteDraft.tagsText),
        attachments,
      }
      const res = editingNoteId
        ? await api.patch(`/work/personal-notes/${editingNoteId}`, payload)
        : await api.post('/work/personal-notes', payload)
      const saved = normalizeNoteFromApi(unwrap(res))
      setNotes((prev) => sortNotes([saved, ...prev.filter((note) => String(note.id) !== String(saved.id))]))
      setNoteDraft(EMPTY_NOTE_FORM)
      setEditingNoteId(null)
      setNoteComposerOpen(false)
    } catch (e) {
      setError(getApiErrorMessage(e, '업무노트를 저장하지 못했습니다.'))
    }
  }

  const editNote = (note) => {
    setNotesPanelOpen(true)
    setNoteComposerOpen(true)
    setEditingNoteId(note.id)
    setNoteDraft({
      title: note.title || '',
      body: note.body || '',
      content: note.content || null,
      attachments: Array.isArray(note.attachments) ? note.attachments : [],
      tagsText: Array.isArray(note.tags) ? note.tags.join(', ') : '',
      pinned: note.pinned === true,
    })
  }

  const cancelNoteEdit = () => {
    setEditingNoteId(null)
    setNoteDraft(EMPTY_NOTE_FORM)
    setNoteComposerOpen(false)
  }

  const openNewNoteComposer = () => {
    setEditingNoteId(null)
    setNoteDraft(EMPTY_NOTE_FORM)
    setNotesPanelOpen(true)
    setNoteComposerOpen(true)
  }

  const toggleNotePin = async (note) => {
    const previous = notes
    const optimistic = { ...note, pinned: !note.pinned, updatedAt: new Date().toISOString() }
    setNotes((prev) => sortNotes([optimistic, ...prev.filter((item) => String(item.id) !== String(note.id))]))
    try {
      const res = await api.patch(`/work/personal-notes/${note.id}`, { pinned: !note.pinned })
      const saved = normalizeNoteFromApi(unwrap(res))
      setNotes((prev) => sortNotes([saved, ...prev.filter((item) => String(item.id) !== String(note.id))]))
    } catch (e) {
      setNotes(previous)
      setError(getApiErrorMessage(e, '노트 고정 상태를 변경하지 못했습니다.'))
    }
  }

  const moveTask = async (taskId, status) => {
    const previous = tasks
    const now = new Date().toISOString()
    const current = tasks.find((task) => String(task.id) === String(taskId))
    if (!current || current.status === status) return
    setTasks((prev) => {
      const current = prev.find((task) => String(task.id) === String(taskId))
      if (!current || current.status === status) return prev
      const updated = {
        ...current,
        status,
        updatedAt: now,
        completedAt: status === 'DONE' ? (current.completedAt || now) : null,
      }
      return [updated, ...prev.filter((task) => String(task.id) !== String(taskId))]
    })
    setError('')
    try {
      const res = await api.patch(`/work/personal-tasks/${taskId}`, { status })
      const saved = normalizeTaskFromApi(unwrap(res))
      setTasks((prev) => [saved, ...prev.filter((task) => String(task.id) !== String(taskId))])
    } catch {
      setTasks(previous)
      setError('업무 상태를 저장하지 못했습니다.')
    }
  }

  const deleteTask = async (taskId) => {
    const previous = tasks
    setTasks((prev) => prev.filter((task) => String(task.id) !== String(taskId)))
    setError('')
    try {
      await api.delete(`/work/personal-tasks/${taskId}`)
    } catch (e) {
      setTasks(previous)
      setError(getApiErrorMessage(e, '업무를 휴지통으로 이동하지 못했습니다.'))
    }
  }

  const openTrash = async () => {
    setTrashOpen(true)
    setTrashLoading(true)
    try {
      const res = await api.get('/work/personal-tasks/trash')
      const data = unwrap(res)
      setTrashTasks(pageItems(data).map(normalizeTaskFromApi))
      setTrashRetentionDays(Number(data?.retentionDays) || 30)
    } catch (e) {
      setError(getApiErrorMessage(e, '휴지통을 불러오지 못했습니다.'))
    } finally {
      setTrashLoading(false)
    }
  }

  const restoreTask = async (taskId) => {
    try {
      const res = await api.post(`/work/personal-tasks/${taskId}/restore`)
      const restored = normalizeTaskFromApi(unwrap(res))
      setTrashTasks((prev) => prev.filter((task) => String(task.id) !== String(taskId)))
      setTasks((prev) => [restored, ...prev.filter((task) => String(task.id) !== String(taskId))])
    } catch (e) {
      setError(getApiErrorMessage(e, '업무를 복원하지 못했습니다.'))
    }
  }

  const permanentlyDeleteTask = async (taskId) => {
    if (!window.confirm('이 업무를 영구 삭제할까요? 삭제 후에는 복원할 수 없습니다.')) return
    try {
      await api.delete(`/work/personal-tasks/${taskId}/permanent`)
      setTrashTasks((prev) => prev.filter((task) => String(task.id) !== String(taskId)))
    } catch (e) {
      setError(getApiErrorMessage(e, '업무를 영구 삭제하지 못했습니다.'))
    }
  }

  const deleteNote = async (noteId) => {
    const previous = notes
    setNotes((prev) => prev.filter((note) => String(note.id) !== String(noteId)))
    setError('')
    try {
      await api.delete(`/work/personal-notes/${noteId}`)
      if (String(editingNoteId) === String(noteId)) cancelNoteEdit()
    } catch (e) {
      setNotes(previous)
      setError(getApiErrorMessage(e, '업무노트를 삭제하지 못했습니다.'))
    }
  }

  return (
    <PageFrame>
      <PageHeader
        title="내 업무보드"
        description={loading ? '불러오는 중' : undefined}
        actions={(
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg bg-sgez-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sgez-primary/90"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-4 w-4" />
            업무 추가
          </button>
          <Link
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            to="/work/calendar"
          >
            일정관리
          </Link>
          <button
            type="button"
            className="inline-flex min-h-11 items-center gap-1.5 justify-center rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            onClick={openTrash}
          >
            <ArchiveRestore className="h-4 w-4" />
            휴지통
          </button>
          <button
            type="button"
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            onClick={() => setCredentialsOpen(true)}
          >
            내 전산코드 보기
          </button>
        </div>
        )}
      />
      <div className="mx-auto w-full max-w-[120rem]">

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      ) : null}

      <TaskCreateModal
        open={createOpen}
        value={form}
        onChange={setForm}
        onSubmit={addTask}
        onClose={() => setCreateOpen(false)}
      />
      <TaskTrashModal
        open={trashOpen}
        tasks={trashTasks}
        loading={trashLoading}
        retentionDays={trashRetentionDays}
        onClose={() => setTrashOpen(false)}
        onRestore={restoreTask}
        onPermanentDelete={permanentlyDeleteTask}
      />
      <InsurerSystemsDirectory
        credentialsOnly
        credentialsOpen={credentialsOpen}
        onCredentialsClose={() => setCredentialsOpen(false)}
      />

      <div className={`grid gap-4 ${notesPanelOpen
        ? 'min-[1800px]:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]'
        : 'min-[1800px]:grid-cols-[minmax(0,9fr)_minmax(0,1fr)]'}`}>
        <div className="min-w-0">
          <MobileTaskList
            tasks={mobileTasks}
            totalCount={tasks.length}
            counts={counts}
            selectedStatus={selectedMobileStatus}
            onSelectStatus={setSelectedMobileStatus}
            onMove={moveTask}
            onDelete={deleteTask}
          />

          <div className="hidden xl:block">
            <DndContext
              sensors={sensors}
              onDragStart={({ active }) => setActiveDragTask(active?.data?.current?.task || null)}
              onDragCancel={() => setActiveDragTask(null)}
              onDragEnd={({ over }) => {
                const task = activeDragTask
                const nextStatus = over?.id
                setActiveDragTask(null)
                if (task?.id && nextStatus && nextStatus !== task.status) moveTask(task.id, nextStatus)
              }}
            >
              <div className="-mx-2 overflow-x-auto px-2 pb-3">
                <div className="flex gap-3">
                  {COLUMNS.map((column) => (
                    <PersonalTaskColumn
                      key={column.key}
                      column={column}
                      tasks={tasks.filter((task) => task.status === column.key)}
                      count={counts[column.key] || 0}
                      activeTaskId={activeDragTask?.id}
                      onMove={moveTask}
                      onDelete={deleteTask}
                    />
                  ))}
                </div>
              </div>
              <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
                {activeDragTask ? <PersonalTaskCard task={activeDragTask} onMove={moveTask} onDelete={deleteTask} overlay /> : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>

        <WorkNotesPanel
          open={notesPanelOpen}
          composerOpen={noteComposerOpen}
          notes={filteredNotes}
          totalCount={notes.length}
          search={noteSearch}
          draft={noteDraft}
          editingNoteId={editingNoteId}
          onSearchChange={setNoteSearch}
          onDraftChange={setNoteDraft}
          onToggleOpen={() => setNotesPanelOpen((value) => !value)}
          onOpenComposer={openNewNoteComposer}
          onAdd={addNote}
          onEdit={editNote}
          onCancelEdit={cancelNoteEdit}
          onTogglePin={toggleNotePin}
          onDelete={deleteNote}
        />
      </div>
      </div>
    </PageFrame>
  )
}

function TaskTrashModal({ open, tasks, loading, retentionDays, onClose, onRestore, onPermanentDelete }) {
  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/40 px-4 py-6">
      <div className="absolute inset-0" onClick={onClose} />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="personal-task-trash-title"
        className="relative flex max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-sgez-border-strong bg-sgez-surface shadow-2xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-sgez-border px-5 py-4">
          <div className="min-w-0">
            <h2 id="personal-task-trash-title" className="text-lg font-bold text-slate-900">휴지통</h2>
            <p className="mt-1 text-xs text-slate-500">삭제한 업무는 {retentionDays}일간 보관됩니다.</p>
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            title="닫기"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-4">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">불러오는 중</div>
          ) : tasks.length === 0 ? (
            <div className="rounded-lg border border-dashed border-sgez-border px-4 py-10 text-center text-sm text-slate-500">
              휴지통이 비어 있습니다.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {tasks.map((task) => (
                <article key={task.id} className="rounded-lg border border-sgez-border bg-sgez-surface p-4">
                  <div className="min-w-0">
                    <h3 className="break-words text-sm font-bold text-slate-900">{task.title}</h3>
                    {task.memo ? (
                      <p className="mt-1 line-clamp-3 whitespace-pre-wrap break-words text-xs leading-5 text-slate-600">{task.memo}</p>
                    ) : null}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] font-semibold text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">{columnLabel(task.status)}</span>
                    {task.deletedAt ? <span>삭제 {formatNoteTime(task.deletedAt)}</span> : null}
                  </div>
                  {task.expiresAt ? (
                    <div className="mt-1 text-[11px] text-slate-400">자동 삭제 {formatNoteTime(task.expiresAt)}</div>
                  ) : null}
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-lg border border-sgez-border-strong px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={() => onRestore(task.id)}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      복원
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                      onClick={() => onPermanentDelete(task.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      영구삭제
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

function WorkNotesPanel({
  open,
  composerOpen,
  notes,
  totalCount,
  search,
  draft,
  editingNoteId,
  onSearchChange,
  onDraftChange,
  onToggleOpen,
  onOpenComposer,
  onAdd,
  onEdit,
  onCancelEdit,
  onTogglePin,
  onDelete,
}) {
  if (!open) {
    return (
      <section className="flex min-h-[12rem] flex-col overflow-hidden rounded-2xl border border-sgez-border-strong bg-sgez-surface-sunken min-[1800px]:sticky min-[1800px]:top-4 min-[1800px]:h-[calc(100vh-12rem)] min-[1800px]:min-h-[38rem]">
        <div className="border-b border-sgez-border bg-sgez-surface-subtle p-2.5">
          <div className="flex items-center gap-1.5">
            <StickyNote className="h-4 w-4 shrink-0 text-sgez-primary" />
            <h2 className="min-w-0 flex-1 truncate text-sm font-black text-slate-900">업무노트</h2>
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-slate-600">{totalCount}</span>
            <button
              type="button"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              title="업무노트 펼치기"
              aria-label="업무노트 펼치기"
              onClick={onToggleOpen}
            >
              <PanelRightOpen className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-lg border border-sgez-border-strong bg-white px-2 py-1.5 text-xs font-bold text-sgez-primary hover:bg-slate-50"
            onClick={onOpenComposer}
          >
            <Plus className="h-3.5 w-3.5" />
            노트 추가
          </button>
        </div>
        <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
          {notes.length === 0 ? (
            <div className="px-1 py-4 text-center text-xs text-slate-400">노트 없음</div>
          ) : notes.map((note) => (
            <button
              key={note.id}
              type="button"
              className="block w-full rounded-lg border border-sgez-border bg-white px-2.5 py-2 text-left hover:border-sgez-primary/40 hover:bg-slate-50"
              title="업무노트 펼치기"
              onClick={onToggleOpen}
            >
              <span className="line-clamp-2 break-words text-xs font-bold leading-4 text-slate-800">
                {note.title || note.body || '첨부 노트'}
              </span>
            </button>
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className="flex min-h-[30rem] flex-col overflow-hidden rounded-2xl border border-sgez-border-strong bg-sgez-surface-sunken min-[1800px]:sticky min-[1800px]:top-4 min-[1800px]:h-[calc(100vh-12rem)] min-[1800px]:min-h-[38rem]">
      <div className="border-b border-sgez-border bg-sgez-surface-subtle px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <StickyNote className="h-4 w-4 shrink-0 text-sgez-primary" />
            <h2 className="truncate text-sm font-black text-slate-900">업무노트</h2>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold tabular-nums text-slate-600">{totalCount}</span>
            {!composerOpen ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg border border-sgez-border-strong bg-white px-2.5 py-1.5 text-xs font-bold text-sgez-primary hover:bg-slate-50"
                onClick={onOpenComposer}
              >
                <Plus className="h-3.5 w-3.5" />
                노트 추가
              </button>
            ) : null}
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              title="업무노트 접기"
              aria-label="업무노트 접기"
              onClick={onToggleOpen}
            >
              <PanelRightClose className="h-4 w-4" />
            </button>
          </div>
        </div>
        {composerOpen ? <form className="mt-3 space-y-2" onSubmit={onAdd}>
          <input
            className="w-full rounded-lg border border-sgez-border-strong bg-sgez-surface px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={draft.title}
            onChange={(event) => onDraftChange({ ...draft, title: event.target.value })}
            maxLength={200}
            placeholder="제목 (선택)"
          />
          <CustomerMemoRichEditor
            uploadPath="/files/upload/customer-memo"
            valueHtml={memoContentToHtml(draft.content, draft.body)}
            plainText={draft.body}
            attachments={draft.attachments}
            placeholder="업무 내용, 체크리스트, 표, 링크, 이미지 등을 입력하세요"
            toolbarMode="full"
            shellClassName="bg-white"
            contentClassName="customer-memo-editor-content min-h-[12rem] max-h-[24rem] overflow-y-auto"
            onChange={(next) => onDraftChange({ ...draft, body: next.plainText, content: next.content })}
            onAttachmentsChange={(attachments) => onDraftChange({ ...draft, attachments })}
          />
          <input
            className="w-full rounded-lg border border-sgez-border-strong bg-sgez-surface px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={draft.tagsText}
            onChange={(event) => onDraftChange({ ...draft, tagsText: event.target.value })}
            placeholder="태그 (쉼표로 구분)"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
              <input
                type="checkbox"
                checked={draft.pinned}
                onChange={(event) => onDraftChange({ ...draft, pinned: event.target.checked })}
              />
              상단 고정
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded-lg border border-sgez-border-strong px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                onClick={onCancelEdit}
              >
                취소
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-1 rounded-lg bg-sgez-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sgez-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!draft.body.trim() && (!Array.isArray(draft.attachments) || draft.attachments.length === 0)}
              >
                <Save className="h-4 w-4" />
                {editingNoteId ? '수정 저장' : '저장'}
              </button>
            </div>
          </div>
        </form> : null}
        <label className="relative mt-3 block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full rounded-lg border border-sgez-border-strong bg-sgez-surface py-2 pl-9 pr-3 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="노트 검색"
          />
        </label>
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
        {notes.length === 0 ? (
          <div className="rounded-xl border border-dashed border-sgez-border bg-sgez-surface/80 px-3 py-8 text-center text-xs text-slate-400">
            {search.trim() ? '검색 결과 없음' : '노트가 없습니다'}
          </div>
        ) : (
          notes.map((note) => (
            <article key={note.id} className={`rounded-xl border bg-sgez-surface p-3 shadow-sm ${note.pinned ? 'border-sgez-primary/40' : 'border-sgez-border'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {note.title ? <h3 className="break-words text-sm font-bold text-slate-900">{note.title}</h3> : null}
                  <div className="mt-1 text-[11px] font-semibold text-slate-400">{formatNoteTime(note.updatedAt || note.createdAt)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    className={`rounded-md p-1 ${note.pinned ? 'text-sgez-primary' : 'text-slate-400 hover:text-slate-700'}`}
                    title={note.pinned ? '고정 해제' : '상단 고정'}
                    onClick={() => onTogglePin(note)}
                  >
                    <Pin className="h-4 w-4" fill={note.pinned ? 'currentColor' : 'none'} />
                  </button>
                  <button
                    type="button"
                    className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    title="수정"
                    onClick={() => onEdit(note)}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title="삭제"
                    onClick={() => onDelete(note.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <CustomerMemoRichContent
                content={note.content}
                plainText={note.body}
                className="customer-memo-editor-content mt-3 break-words text-sm leading-5 text-slate-700"
              />
              {Array.isArray(note.attachments) && note.attachments.length > 0 ? (
                <div className="mt-3">
                  <CustomerMemoAttachmentList attachments={note.attachments} disabled />
                </div>
              ) : null}
              {Array.isArray(note.tags) && note.tags.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {note.tags.map((tag) => (
                    <span key={`${note.id}-${tag}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{tag}</span>
                  ))}
                </div>
              ) : null}
            </article>
          ))
        )}
      </div>
    </section>
  )
}

function MobileTaskList({ tasks, totalCount, counts, selectedStatus, onSelectStatus, onMove, onDelete }) {
  const selectedLabel = selectedStatus === 'ALL' ? '전체' : columnLabel(selectedStatus)
  return (
    <section className="xl:hidden">
      <div className="rounded-xl border border-sgez-border-strong bg-sgez-surface p-3 shadow-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-slate-600">조회 상태</span>
          <select
            className="w-full rounded-lg border border-sgez-border-strong bg-sgez-surface px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={selectedStatus}
            onChange={(event) => onSelectStatus(event.target.value)}
          >
            {STATUS_FILTERS.map((filter) => {
              const count = filter.key === 'ALL' ? totalCount : counts[filter.key] || 0
              return (
                <option key={filter.key} value={filter.key}>
                  {filter.label} {count}
                </option>
              )
            })}
          </select>
        </label>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-black text-slate-900">{selectedLabel}</h2>
        <span className="text-xs font-semibold tabular-nums text-slate-500">{tasks.length}건</span>
      </div>
      <div className="mt-2 flex flex-col gap-2">
        {tasks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-sgez-border bg-sgez-surface px-3 py-8 text-center text-xs text-slate-400">
            {selectedLabel} 비어 있음
          </div>
        ) : (
          tasks.map((task) => (
            <PersonalTaskCard
              key={task.id}
              task={task}
              onMove={onMove}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </section>
  )
}

function PersonalTaskColumn({ column, tasks, count, activeTaskId, onMove, onDelete }) {
  const { isOver, setNodeRef } = useDroppable({ id: column.key })
  return (
    <section
      ref={setNodeRef}
      className={`flex h-[calc(100vh-12rem)] min-h-[38rem] w-[14rem] shrink-0 flex-col overflow-hidden rounded-2xl border bg-sgez-surface-sunken transition min-[1400px]:w-[15rem] min-[1920px]:w-[18rem] ${
        isOver ? 'border-sgez-primary ring-2 ring-sgez-primary/20' : 'border-sgez-border-strong'
      }`}
    >
      <div className="flex items-center justify-between border-b border-sgez-border bg-sgez-surface-subtle px-3 py-2.5">
        <h2 className="text-sm font-black text-slate-900">{column.label}</h2>
        <span className="rounded-full bg-sgez-surface px-2 py-0.5 text-xs font-bold tabular-nums text-slate-600">{count}</span>
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2.5">
        {tasks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-sgez-border bg-sgez-surface/70 px-3 py-8 text-center text-xs text-slate-400">
            비어 있음
          </div>
        ) : (
          tasks.map((task) => (
            <DraggablePersonalTaskCard
              key={task.id}
              task={task}
              isActive={String(activeTaskId || '') === String(task.id)}
              onMove={onMove}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </section>
  )
}

function DraggablePersonalTaskCard({ task, isActive, onMove, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `personal-task-${task.id}`,
    data: { task },
  })
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging || isActive ? 0.35 : 1,
    touchAction: 'none',
  }
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <PersonalTaskCard task={task} onMove={onMove} onDelete={onDelete} />
    </div>
  )
}

function PersonalTaskCard({ task, onMove, onDelete, overlay = false }) {
  const linkIsInternal = task.linkUrl && task.linkUrl.startsWith('/')
  const linkLabel = task.linkLabel || task.linkUrl
  return (
    <article
      className={`rounded-xl border border-sgez-border bg-sgez-surface p-3 shadow-sm ${overlay ? 'w-[18rem] rotate-1 cursor-grabbing shadow-2xl' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="break-words text-sm font-black text-slate-900">{task.title}</h3>
          {task.memo ? <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-slate-600">{task.memo}</p> : null}
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
          title="삭제"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation()
            onDelete(task.id)
          }}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${priorityClass(task.priority)}`}>
          {PRIORITIES.find((item) => item.value === task.priority)?.label || '보통'}
        </span>
        {task.dueDate ? (
          <span className={`text-[11px] font-semibold ${dueTone(task.dueDate, task.status)}`}>{task.dueDate}</span>
        ) : null}
      </div>
      {task.linkUrl ? (
        <div className="mt-3">
          {task.linkType && task.linkType !== 'NONE' ? (
            <span className="mb-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
              {linkTypeLabel(task.linkType)}
            </span>
          ) : null}
          {linkIsInternal ? (
            <Link
              className="inline-flex max-w-full items-center gap-1 truncate text-xs font-semibold text-sgez-primary hover:underline"
              to={task.linkUrl}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{linkLabel}</span>
            </Link>
          ) : (
            <a
              className="inline-flex max-w-full items-center gap-1 truncate text-xs font-semibold text-sgez-primary hover:underline"
              href={task.linkUrl}
              target="_blank"
              rel="noreferrer"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{linkLabel}</span>
            </a>
          )}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end">
        <select
          className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 sm:max-w-[9rem]"
          value={task.status}
          onPointerDown={(event) => event.stopPropagation()}
          onChange={(event) => {
            if (event.target.value !== task.status) onMove(task.id, event.target.value)
          }}
        >
          {COLUMNS.map((column) => (
            <option key={column.key} value={column.key}>{column.label}</option>
          ))}
        </select>
      </div>
    </article>
  )
}
